import { useTranslation } from 'react-i18next';
import ReactECharts from 'echarts-for-react';
import * as echarts from 'echarts/core';
import { FunnelChart } from 'echarts/charts';
import Hidden from 'raydiant-elements/layout/Hidden';
import { Popover } from '@headlessui/react';
import { ContentDetailSummary } from '@raydiant/api-client-js';
import { abbreviateNumber } from 'js-abbreviation-number';

import ChartCard from '../ChartCard/ChartCard';
import QuestionMarkIcon from '../../../components/QuestionMarkIcon';
import { CHART_DEFAULT_OPTIONS } from '../utils';
import config from '../../../config';

const { supportAppUrl } = config;

echarts.use([FunnelChart]);

interface ConversionFunnelChartProps {
  contentData: ContentDetailSummary;
}

const colors = ['#FA8700', '#FDDA5F', '#F1E2B1', '#FAFAD2'];

const ConversionFunnelChart = ({ contentData }: ConversionFunnelChartProps) => {
  const { t } = useTranslation();

  const isEmptyData =
    !contentData.ots &&
    !contentData.impressions &&
    !contentData.verifiedViews &&
    !contentData.engagedViews;

  const dataSet = [
    { value: contentData.ots, name: 'Traffic/OTS' },
    { value: contentData.impressions, name: 'Impressions' },
    { value: contentData.verifiedViews, name: 'Content Views' },
    { value: contentData.engagedViews, name: 'Engaged Views' },
  ];

  const options = {
    ...CHART_DEFAULT_OPTIONS,
    color: colors,
    tooltip: {
      trigger: 'item',
    },
    legend: {
      show: false,
    },
    series: [
      {
        name: '',
        type: 'funnel',
        top: 30,
        left: 30,
        min: 0,
        max: Math.max(
          ...[
            contentData.ots ?? 0,
            contentData.impressions ?? 0,
            contentData.verifiedViews ?? 0,
            contentData.engagedViews ?? 0,
          ],
        ),
        minSize: '45%',
        maxSize: '100%',
        sort: 'none',
        gap: 2,
        label: {
          show: true,
          position: 'inside',
          formatter: function (params: any) {
            return `{a|${params.name}} \n {b|${abbreviateNumber(
              params.value,
            )}}`;
          },
          rich: {
            a: {
              color: '#000',
              fontSize: 14,
              fontWeight: 600,
            },
            b: {
              color: '#000',
              fontSize: 12,
              fontWeight: 400,
            },
          },
        },
        labelLine: {
          length: 30,
          lineStyle: {
            width: 4,
            type: 'solid',
            color: '#D9D9D9',
          },
        },
        emphasis: {
          label: {
            position: 'inside',
          },
        },
        itemStyle: {
          borderColor: '#fff',
          borderWidth: 1,
        },
        data: dataSet,
      },
    ],
  };

  const mobileOptions = {
    ...options,
    legend: {
      show: false,
    },
    series: [
      {
        ...options.series[0],
        top: 0,
        left: 20,
        bottom: 20,
        width: 290,
        label: {
          show: true,
          position: 'inside',
          formatter: function (params: any) {
            return `{a|${params.name}} \n {b|${abbreviateNumber(
              params.value,
            )}}`;
          },
          rich: {
            a: {
              color: '#000',
              fontSize: 14,
              fontWeight: 600,
            },
            b: {
              color: '#000',
              fontSize: 12,
              fontWeight: 400,
            },
          },
        },
        labelLine: {
          show: false,
        },
      },
    ],
  };

  return (
    <ChartCard
      subTitle={''}
      title={
        <div className="flex gap-3 items-center">
          <h2 className="text-black text-xl font-bold">
            {t('analytics.contentDetailPage.conversionFunnel.title')}
          </h2>
          <Popover className="relative">
            <Popover.Button>
              <QuestionMarkIcon />
            </Popover.Button>
            <Popover.Panel className="absolute z-10 card popover">
              <div className="overflow-hidden">
                <p className="mb-3">
                  {t('analytics.contentDetailPage.conversionFunnel.tooltip')}
                </p>
                <a
                  href={`${supportAppUrl}/visitor-insight/5imknKqrccCD9gmD6gKuJ1/how-do-i-use-the-insight-analytics-platform/6RjNYsyCvxraPhuSNUMYQo`}
                  rel="noreferrer"
                  target="_blank"
                >
                  {t('analytics.learnmore')}
                </a>
              </div>
            </Popover.Panel>
          </Popover>
        </div>
      }
      showTimeInterval={false}
    >
      {isEmptyData ? (
        <div className="w-full text-center">
          {t('analytics.contentDetailPage.emptyData.emptyChartData')}
        </div>
      ) : (
        <div className="min-h-full">
          <Hidden xsDown>
            <ReactECharts
              style={{ height: '460px' }}
              option={options}
              onChartReady={(chart) => chart.resize()}
            />
          </Hidden>
          <Hidden smUp>
            <ReactECharts
              style={{ height: '460px' }}
              option={mobileOptions}
              onChartReady={(chart) => chart.resize()}
            />
          </Hidden>
        </div>
      )}
    </ChartCard>
  );
};

export default ConversionFunnelChart;
